import { useCallback, useEffect, useState } from 'react';
import { AnnotationsForGrid, ImageInfo, PathContextType } from '../../annotation.types';
import { useKeyPress } from '../../../../Shared/UseKeyPress';
import { getImageNameWithoutExtensions } from '../../../../../shared/Helpers';
import { ValidationModalDto } from '../../AnnotationValidationModal/AnnotationValidationModal.slice';
import { DirectionOptions, Direction, getNextImage } from '../helpers';

export default function UseImagesHelpers(
  sequencePath: string,
  selectedName: string,
  imageLoading: boolean,
  images: ImageInfo[] | null,
  showCreateAnnotationModal: boolean,
  validationModalData: ValidationModalDto,
  presentAnnotations: AnnotationsForGrid[] | null,
  saveFinding: boolean,
  pathContext: PathContextType,
  onSelectImage: (url: string, name: string) => void,
  onGetImageAnnotations: (isPhotocard: boolean) => void,
  onSaveFinding: (saveFinding: boolean) => void,
  onSetIsPhotocardSelected: (isSelected: boolean) => void,
  navigateToImage: (name: string) => void,
  onClearToolbar: () => void
) {
  const [pressedDown, setPressedDown] = useState(false);
  const [pressedUp, setPressedUp] = useState(false);
  const [processKeyEvent, setProcessKeyEvent] = useState(false);

  const selectImage = useCallback(
    (name: string, isPhotocard: boolean) => {
      if (imageLoading || name === selectedName) {
        return;
      }

      onClearToolbar();

      const url = `sequence/image/${sequencePath}/${name}`;

      onSelectImage(url, name);
      onGetImageAnnotations(isPhotocard);
    },
    [imageLoading, onClearToolbar, onGetImageAnnotations, onSelectImage, selectedName, sequencePath]
  );

  const onListImageSelected = useCallback(
    (name: string, isPhotocard: boolean) => {
      navigateToImage(name);
      onSetIsPhotocardSelected(isPhotocard);
    },
    [navigateToImage, onSetIsPhotocardSelected]
  );

  const onImageObjectSelected = useCallback(
    async (obj?: fabric.Object) => {
      if (obj && obj.name && images) {
        if (obj.name.toString() === selectedName) {
          return;
        }

        const { name } = obj;
        const selectedImage = images.find(item => getImageNameWithoutExtensions(item.filename) === getImageNameWithoutExtensions(name));
        if (selectedImage) {
          navigateToImage(name);
        }
      } else {
        navigateToImage('');
      }
    },
    [images, navigateToImage, selectedName]
  );

  const processPressEvent = useCallback(
    (images: ImageInfo[] | null, upDown: Direction) => {
      if (pathContext === 'no-images') {
        return;
      }

      if (!(images && images.length && !(showCreateAnnotationModal && !validationModalData.showModal))) {
        return;
      }

      if (presentAnnotations && presentAnnotations.some(x => x.dirty)) {
        setProcessKeyEvent(true);
        onSaveFinding(true);

        if (upDown === 1) {
          setPressedDown(true);
        } else {
          setPressedUp(true);
        }

        return;
      }

      const nextImage = getNextImage(images, selectedName, upDown);

      if (!nextImage) {
        return;
      }

      navigateToImage(nextImage.filename);
      onSetIsPhotocardSelected(nextImage.isPhotocard);
    },
    [
      navigateToImage,
      onSaveFinding,
      onSetIsPhotocardSelected,
      presentAnnotations,
      selectedName,
      showCreateAnnotationModal,
      validationModalData.showModal,
      pathContext
    ]
  );

  useEffect(() => {
    if (!saveFinding) {
      setProcessKeyEvent(false);
    }
  }, [saveFinding]);

  useEffect(() => {
    if ((pressedDown || pressedUp) && !processKeyEvent) {
      if (pressedUp) {
        processPressEvent(images, DirectionOptions.UP);
        setPressedUp(false);
      }

      if (pressedDown) {
        processPressEvent(images, DirectionOptions.DOWN);
        setPressedDown(false);
      }
    }
  }, [images, pressedDown, pressedUp, processKeyEvent, processPressEvent]);

  useKeyPress('ArrowDown', false, () => {
    processPressEvent(images, DirectionOptions.DOWN);
  });
  useKeyPress('ArrowUp', false, () => {
    processPressEvent(images, DirectionOptions.UP);
  });

  return {
    onImageObjectSelected,
    onListImageSelected,
    selectImage
  };
}
